import {
  ResponseDatetimeSubTypeEnum,
  ResponseTextSubTypeEnum,
  ResponseTypeEnum,
  TemplateQuestion,
} from '@ulysses-inc/harami_api_client'

/**
 * 回答検索に対応している質問種別
 */
const TRACEABLE_RESPONSE_TYPES = [
  ResponseTypeEnum.RESPONSE_SET,
  ResponseTypeEnum.DATETIME,
  ResponseTypeEnum.TEXT,
] as const satisfies readonly ResponseTypeEnum[]

/**
 * 回答検索に対応している日時フォーマット
 */
const TRACEABLE_DATETIME_SUB_TYPES = [
  ResponseDatetimeSubTypeEnum.DATE,
  ResponseDatetimeSubTypeEnum.DATETIME,
] as const satisfies readonly ResponseDatetimeSubTypeEnum[]

/**
 * 回答検索に対応しているテキストフォーマット
 */
const TRACEABLE_TEXT_SUB_TYPES = [
  ResponseTextSubTypeEnum.SINGLE_LINE,
] as const satisfies readonly ResponseTextSubTypeEnum[]

type TraceableResponseType = (typeof TRACEABLE_RESPONSE_TYPES)[number]
type TraceableDatetimeSubType = (typeof TRACEABLE_DATETIME_SUB_TYPES)[number]
type TraceableTextSubType = (typeof TRACEABLE_TEXT_SUB_TYPES)[number]

/**
 * 質問種別が回答検索に対応しているか判定する型ガード関数
 */
const isTraceableResponseType = (
  responseType: ResponseTypeEnum,
): responseType is TraceableResponseType => {
  return TRACEABLE_RESPONSE_TYPES.includes(
    responseType as TraceableResponseType,
  )
}

/**
 * 日時質問のフォーマットが回答検索に対応しているか判定する型ガード関数
 */
const isTraceableDatetimeSubType = (
  subType: ResponseDatetimeSubTypeEnum,
): subType is TraceableDatetimeSubType => {
  return TRACEABLE_DATETIME_SUB_TYPES.includes(
    subType as TraceableDatetimeSubType,
  )
}

/**
 * テキスト質問のフォーマットが回答検索に対応しているか判定する型ガード関数
 */
const isTraceableTextSubType = (
  subType: ResponseTextSubTypeEnum,
): subType is TraceableTextSubType => {
  return TRACEABLE_TEXT_SUB_TYPES.includes(subType as TraceableTextSubType)
}

/**
 * 質問種別が検索キーに対応しているか判定する
 *
 * @param question - 判定対象の質問
 * @returns 検索キーに対応している場合はtrue
 *
 */
export const isTraceableQuestionType = (
  question?: TemplateQuestion,
): boolean => {
  if (!question) {
    return false
  }
  const responseType = question.responseType
  if (!responseType) {
    return false
  }
  // 回答検索が利用可能な質問種別かチェック
  if (!isTraceableResponseType(responseType)) {
    return false
  }

  switch (responseType) {
    case ResponseTypeEnum.RESPONSE_SET:
      return true

    case ResponseTypeEnum.DATETIME: {
      // responseDatetimesは配列だが、プロダクトでは最初の1件のみを扱っている
      const subType = question.responseDatetimes?.[0]?.subType
      return subType !== undefined && isTraceableDatetimeSubType(subType)
    }

    case ResponseTypeEnum.TEXT: {
      // responseTextsは配列だが、プロダクトでは最初の1件のみを扱っている
      const subType = question.responseTexts?.[0]?.subType
      return subType !== undefined && isTraceableTextSubType(subType)
    }

    default: {
      const _exhaustiveCheck: never = responseType
      return _exhaustiveCheck
    }
  }
}
